import React, { useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import CrudTable from "../components/CrudTable";
import api from "../lib/api";
import { Factory, RefreshCw, Loader2, FileDown, Printer, AlertTriangle } from "lucide-react";

const trTarih = (s) => {
  if (!s) return "—";
  const p = String(s).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : s;
};
const fmt = (n) => (n == null || n === "" ? "0" : Number(n).toLocaleString("tr-TR"));
const damgaStr = () => new Date().toLocaleString("tr-TR").replace(/[/:]/g, "-");
const escHtml = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const MetricBox = ({ label, val, cls }) => (
  <div className="bg-zinc-50 rounded-md px-2 py-1.5 text-center border border-zinc-100">
    <div className="text-[9px] uppercase tracking-wide text-zinc-400 leading-none truncate">{label}</div>
    <div className={`text-sm font-bold leading-tight mt-0.5 ${cls}`}>{fmt(val)}</div>
  </div>
);

const durumOf = (r) => {
  const sek = Number(r.sekillenecek_adet) || 0;
  const net = Number(r.net_sonra) || 0;
  const kalan = Number(r.kalan_sekillendirme) || 0;
  if (r.kalip_sokulme_tarihi) return "Söküldü";
  if (sek > 0 && kalan <= 0) return "Tamamlandı";
  if (net > 0) return "Üretimde";
  return "Beklemede";
};
const DURUM_CLS = {
  "Tamamlandı": "bg-emerald-100 text-emerald-700 border border-emerald-200",
  "Üretimde": "bg-amber-100 text-amber-700 border border-amber-200",
  "Beklemede": "bg-zinc-200 text-zinc-600 border border-zinc-300",
  "Söküldü": "bg-slate-700 text-white border border-slate-700",
};
const DPDF = { "Tamamlandı": ["#d1fae5", "#047857"], "Üretimde": ["#fef3c7", "#b45309"], "Beklemede": ["#e4e4e7", "#52525b"], "Söküldü": ["#334155", "#ffffff"] };
const DurumBadge = ({ r, testid }) => {
  const d = durumOf(r);
  return <span data-testid={testid} className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap ${DURUM_CLS[d]}`}>{d}</span>;
};

// Tahmini kalıp değişim tarihini en yakın termin ile karşılaştırır
const gecikmeInfo = (r) => {
  const t = r.tahmini_kalip_degisim_tarihi, termin = r.en_yakin_termin;
  if (!t || !termin) return null;
  const dt = new Date(String(t).slice(0, 10)), dter = new Date(String(termin).slice(0, 10));
  if (isNaN(dt) || isNaN(dter)) return null;
  const diff = Math.round((dter - dt) / 86400000);
  if (diff < 0) return { level: "red", termin, text: "Gecikme riski" };
  if (diff <= 7) return { level: "amber", termin, text: "Termine yakın" };
  return { level: "ok", termin };
};

const parenNum = (s) => {
  const m = String(s).match(/\((\d+)\)\s*$/);
  if (m) return parseInt(m[1], 10);
  const m2 = String(s).match(/(\d+)\s*$/);
  return m2 ? parseInt(m2[1], 10) : 9999;
};
const gunTxt = (r) => (r.calisma_vardiya == null ? "" : `${r.calisma_vardiya} vardiya${r.calisma_devam ? " (devam)" : ""}`);
const stripParen = (s) => String(s).replace(/\s*\(\d+\)\s*$/, "").trim();

function groupByMakina(rows) {
  const groups = {};
  rows.forEach((r) => {
    const full = (r.makina || "Tanımsız").trim();
    const base = stripParen(full) || "Tanımsız";
    const key = base.toLowerCase();
    if (!groups[key]) groups[key] = { name: base, items: [] };
    groups[key].items.push(r);
  });
  return Object.values(groups)
    .map((g) => ({ name: g.name, items: [...g.items].sort((a, b) => parenNum(a.makina) - parenNum(b.makina)) }))
    .sort((a, b) => a.name.localeCompare(b.name, "tr", { numeric: true }));
}

function MakineBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/sekillendirme-plani/list").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const groups = groupByMakina(rows);

  const isIzo = (g) => /^makina\s*0?[1-9](?!\d)/i.test(g.name);
  const SINIF = {
    izo: { title: "İzostatik Presler", groups: groups.filter(isIzo) },
    pls: { title: "Plastik Şekillendirme", groups: groups.filter((g) => !isIzo(g)) },
  };
  const EXP_COLS = [
    ["Makine", (r) => stripParen(r.makina || "")],
    ["Sıra", (r) => (parenNum(r.makina) === 9999 ? "" : parenNum(r.makina))],
    ["Kalıp Kodu", (r) => r.kalip_kodu || ""],
    ["Kalıp Bağlanma Tarihi", (r) => trTarih(r.kalip_degisim_tarihi)],
    ["Kalıp Sökülme Tarihi", (r) => trTarih(r.kalip_sokulme_tarihi)],
    ["Çalışma Vardiya", (r) => gunTxt(r)],
    ["Tahmini Kalıp Değişim Tarihi", (r) => trTarih(r.tahmini_kalip_degisim_tarihi)],
    ["Şekillenecek Adet", (r) => Number(r.sekillenecek_adet) || 0],
    ["Bağlanma Sonrası Şekillenen", (r) => Number(r.sekillenen_sonra) || 0],
    ["Bağlanma Sonrası Net", (r) => Number(r.net_sonra) || 0],
    ["Kalan Şekillendirme", (r) => Number(r.kalan_sekillendirme) || 0],
    ["Toplam Kalan Sipariş", (r) => Number(r.toplam_kalan_siparis) || 0],
    ["Güncel Bisküvi Stok", (r) => Number(r.guncel_biskuvi_stok) || 0],
    ["Ham Stok", (r) => Number(r.ham_stok) || 0],
    ["Durum", (r) => durumOf(r)],
  ];

  const exportExcel = ({ title, groups }) => {
    const flat = groups.flatMap((g) => g.items);
    const head = EXP_COLS.map((c) => `<th style="background:#18181B;color:#fff;font-weight:bold;">${escHtml(c[0])}</th>`).join("");
    const body = flat.map((r) => `<tr>${EXP_COLS.map((c) => `<td>${escHtml(c[1](r))}</td>`).join("")}</tr>`).join("");
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><tr><td colspan="${EXP_COLS.length}"><b>Şekillendirme Planı — ${escHtml(title)}</b></td></tr><tr>${head}</tr>${body}</table></body></html>`;
    const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `Şekillendirme Planı - ${title} - ${damgaStr()}.xls`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = ({ title, groups }) => {
    const w = window.open("", "_blank"); if (!w) return;
    const sections = groups.map((g) => {
      const rowsHtml = g.items.map((r) => { const _d = durumOf(r); const _dc = DPDF[_d]; return `<tr>
        <td class="r">${parenNum(r.makina) === 9999 ? "•" : parenNum(r.makina)}</td>
        <td class="mono">${escHtml(r.kalip_kodu)}</td>
        <td>${trTarih(r.kalip_degisim_tarihi)}</td>
        <td style="color:#334155;font-weight:600">${trTarih(r.kalip_sokulme_tarihi)}</td>
        <td class="r" style="color:${r.calisma_devam ? "#b45309" : "#334155"};font-weight:bold">${escHtml(gunTxt(r))}</td>
        <td style="color:#7e22ce">${trTarih(r.tahmini_kalip_degisim_tarihi)}</td>
        <td class="r" style="color:#4338ca">${fmt(r.sekillenecek_adet)}</td>
        <td class="r" style="color:#b45309">${fmt(r.sekillenen_sonra)}</td>
        <td class="r" style="color:#047857">${fmt(r.net_sonra)}</td>
        <td class="r" style="color:${(Number(r.kalan_sekillendirme) || 0) > 0 ? "#dc2626" : "#047857"};font-weight:bold">${fmt(r.kalan_sekillendirme)}</td>
        <td class="r" style="color:#dc2626">${fmt(r.toplam_kalan_siparis)}</td>
        <td class="r" style="color:${(Number(r.guncel_biskuvi_stok) || 0) < 0 ? "#dc2626" : "#0369a1"}">${fmt(r.guncel_biskuvi_stok)}</td>
        <td class="r" style="color:${(Number(r.ham_stok) || 0) < 0 ? "#dc2626" : "#0f766e"}">${fmt(r.ham_stok)}</td>
        <td><span style="background:${_dc[0]};color:${_dc[1]};padding:2px 8px;border-radius:9px;font-weight:700;font-size:10px">${_d}</span></td></tr>`; }).join("");
      const tSek = g.items.reduce((a, b) => a + (Number(b.sekillenecek_adet) || 0), 0);
      const tKalan = g.items.reduce((a, b) => a + Math.max(0, Number(b.kalan_sekillendirme) || 0), 0);
      const tSekl = g.items.reduce((a, b) => a + (Number(b.sekillenen_sonra) || 0), 0);
      const tNet = g.items.reduce((a, b) => a + (Number(b.net_sonra) || 0), 0);
      return `<div class="mk"><h2><span>${escHtml(g.name)}</span><span class="cnt">${g.items.length} ürün</span></h2>
        <table><thead><tr><th class="r">Sıra</th><th>Kalıp Kodu</th><th>Bağlanma</th><th>Sökülme</th><th class="r">Çalışma Vardiya</th><th>Tahmini Değişim</th><th class="r">Şekillenecek</th><th class="r">Şekillenen</th><th class="r">Net</th><th class="r">Kalan Şek.</th><th class="r">Kalan Sipariş</th><th class="r">Bisküvi Stok</th><th class="r">Ham Stok</th><th>Durum</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr><td colspan="6">TOPLAM</td><td class="r">${fmt(tSek)}</td><td class="r">${fmt(tSekl)}</td><td class="r">${fmt(tNet)}</td><td class="r">${fmt(tKalan)}</td><td></td><td></td><td></td><td></td></tr></tfoot></table></div>`;
    }).join("");
    w.document.write(`<!DOCTYPE html><html lang="tr"><head><meta charset="utf-8"><title>Şekillendirme Planı - ${escHtml(title)} - ${damgaStr()}</title>
      <style>@page{size:A4 landscape;margin:12mm}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}body{font-family:'Segoe UI',Arial,sans-serif;color:#18181B;margin:0}
      .hdr{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #d97706;padding-bottom:12px;margin-bottom:14px}
      .hdr .l{display:flex;align-items:center;gap:14px}.hdr img{height:52px}.hdr h1{font-size:20px;margin:0}.hdr .sub{font-size:12px;color:#6b7280;margin-top:2px}
      .hdr .meta{text-align:right;font-size:11px;color:#6b7280;line-height:1.6}
      .mk{margin-bottom:18px;break-inside:avoid}
      .mk h2{font-size:14px;background:#18181B;color:#fff;padding:7px 12px;margin:0;border-radius:6px 6px 0 0;display:flex;justify-content:space-between;align-items:center}
      .mk h2 .cnt{font-size:11px;background:#f59e0b;color:#18181B;border-radius:9px;padding:1px 8px;font-weight:800}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th{background:#f4f4f5;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase;border-bottom:1px solid #ddd}
      td{border-bottom:1px solid #eee;padding:5px 8px}.r{text-align:right}.mono{font-family:'Courier New',monospace;font-weight:bold}
      tfoot td{border-top:2px solid #18181B;font-weight:800;background:#f4f4f5}
      .foot{margin-top:16px;font-size:10px;color:#9ca3af;text-align:center;border-top:1px solid #eee;padding-top:8px}</style></head>
      <body>
      <div class="hdr"><div class="l"><img src="${window.location.origin}/by-bone-logo.png"/><div><h1>Şekillendirme Planı — ${escHtml(title)}</h1><div class="sub">BY BONE PORSELEN — Üretim Takip Sistemi</div></div></div>
      <div class="meta">Tarih: ${new Date().toLocaleString("tr-TR")}<br/>Makine sayısı: ${groups.length}</div></div>
      ${sections}
      <div class="foot">Bu rapor BY BONE Üretim Takip Sistemi tarafından otomatik oluşturulmuştur.</div>
      </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };

  return (
    <div className="mt-12 px-6 pb-10" data-testid="makine-board">
      <div className="flex items-center justify-between mb-5 border-t border-zinc-200 pt-8 flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <Factory className="text-amber-600" size={22} />
          <div>
            <h3 className="font-head font-black uppercase tracking-[0.15em] text-zinc-800 text-lg leading-none">Makine Bazında Şekillendirme Planı</h3>
            <p className="text-xs text-zinc-400 mt-1">Aynı makinenin ürünleri birlikte gruplanır; makineler parantez içindeki numaraya göre sıralanır</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {Object.entries(SINIF).map(([k, s]) => (
            <div key={k} className="flex items-center gap-1.5 border border-zinc-200 rounded-sm pl-2.5 pr-1 py-1 bg-white">
              <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-600">{s.title} <span className="text-zinc-400">({s.groups.length})</span></span>
              <button onClick={() => exportExcel(s)} disabled={!s.groups.length} data-testid={`board-excel-${k}`} className="flex items-center gap-1 bg-green-700 hover:bg-green-800 disabled:opacity-40 text-white text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-colors"><FileDown size={13} /> Excel</button>
              <button onClick={() => exportPdf(s)} disabled={!s.groups.length} data-testid={`board-pdf-${k}`} className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-900 disabled:opacity-40 text-white text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-colors"><Printer size={13} /> PDF</button>
            </div>
          ))}
          <button onClick={load} data-testid="board-refresh" className="flex items-center gap-1.5 text-sm text-zinc-600 hover:text-amber-700 font-semibold transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Yenile
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-400 py-16 text-center"><Loader2 className="animate-spin inline" size={20} /> Yükleniyor...</div>
      ) : groups.length === 0 ? (
        <div className="text-zinc-400 py-16 text-center border border-dashed border-zinc-300 rounded-xl">Henüz plan kaydı yok. Yukarıdan ekleyin.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {groups.map(({ name, items }) => {
            const totSek = items.reduce((a, b) => a + (Number(b.sekillenecek_adet) || 0), 0);
            const totKalan = items.reduce((a, b) => a + Math.max(0, Number(b.kalan_sekillendirme) || 0), 0);
            const totSekl = items.reduce((a, b) => a + (Number(b.sekillenen_sonra) || 0), 0);
            const totNet = items.reduce((a, b) => a + (Number(b.net_sonra) || 0), 0);
            const sorted = items;
            return (
              <div key={name} data-testid={`makine-card-${name}`} className="rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                <div className="flex items-center justify-between bg-zinc-900 px-4 py-3">
                  <div className="flex items-center gap-2 text-white font-bold text-sm truncate">
                    <Factory size={15} className="text-amber-400 shrink-0" />
                    <span className="truncate">{name}</span>
                  </div>
                  <span className="text-[10px] font-black bg-amber-500 text-zinc-900 rounded-full px-2.5 py-0.5 shrink-0">{items.length} ÜRÜN</span>
                </div>
                <div className="divide-y divide-zinc-100">
                  {sorted.map((r, i) => {
                    const sek = Number(r.sekillenecek_adet) || 0;
                    const net = Number(r.net_sonra) || 0;
                    const pct = sek > 0 ? Math.min(100, Math.round(net / sek * 100)) : 0;
                    const kalan = Number(r.kalan_sekillendirme) || 0;
                    return (
                      <div key={i} className="px-4 py-3 hover:bg-amber-50/50 transition-colors" data-testid={`makine-urun-${r.kalip_kodu}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="shrink-0 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded bg-zinc-900 text-white text-[10px] font-black" data-testid={`urun-sira-${r.kalip_kodu}`}>{parenNum(r.makina) === 9999 ? "•" : parenNum(r.makina)}</span>
                            <span className="mono font-bold text-zinc-800 text-sm truncate">{r.kalip_kodu}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[11px] text-zinc-400 whitespace-nowrap" data-testid={`card-tarih-${r.id}`}>
                              {trTarih(r.kalip_degisim_tarihi)}{r.kalip_sokulme_tarihi ? ` → ${trTarih(r.kalip_sokulme_tarihi)}` : ""}
                              {r.calisma_vardiya != null && <span className={`ml-1 font-bold ${r.calisma_devam ? "text-amber-600" : "text-slate-700"}`}>· {gunTxt(r)}</span>}
                            </span>
                            <DurumBadge r={r} testid={`board-durum-${r.kalip_kodu}`} />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          <MetricBox label="Şekillenecek" val={r.sekillenecek_adet} cls="text-indigo-700" />
                          <MetricBox label="Şekillenen" val={r.sekillenen_sonra} cls="text-amber-700" />
                          <MetricBox label="Net" val={r.net_sonra} cls="text-green-700" />
                          <MetricBox label="Kalan Şek." val={r.kalan_sekillendirme} cls={kalan > 0 ? "text-red-600" : "text-green-700"} />
                          <MetricBox label="Kalan Sipariş" val={r.toplam_kalan_siparis} cls="text-red-600" />
                          <MetricBox label="Bisküvi Stok" val={r.guncel_biskuvi_stok} cls={(Number(r.guncel_biskuvi_stok) || 0) < 0 ? "text-red-600" : "text-sky-700"} />
                          <MetricBox label="Ham Stok" val={r.ham_stok} cls={(Number(r.ham_stok) || 0) < 0 ? "text-red-600" : "text-teal-700"} />
                        </div>
                        {durumOf(r) !== "Tamamlandı" && r.tahmini_kalip_degisim_tarihi && (() => {
                          const g = gecikmeInfo(r);
                          const box = g?.level === "red" ? "bg-red-50 border-red-200" : g?.level === "amber" ? "bg-amber-50 border-amber-200" : "bg-purple-50 border-purple-100";
                          const lbl = g?.level === "red" ? "text-red-500" : g?.level === "amber" ? "text-amber-600" : "text-purple-400";
                          const val = g?.level === "red" ? "text-red-700" : g?.level === "amber" ? "text-amber-700" : "text-purple-700";
                          return (
                            <div className={`mt-2 rounded-md border px-2 py-1 ${box}`} data-testid={`tahmini-degisim-${r.kalip_kodu}`}>
                              <div className="flex items-center justify-between">
                                <span className={`text-[10px] uppercase tracking-wide font-semibold ${lbl}`}>Tahmini Kalıp Değişim</span>
                                <span className={`text-xs font-bold mono ${val}`}>{trTarih(r.tahmini_kalip_degisim_tarihi)}</span>
                              </div>
                              {g && g.level !== "ok" && (
                                <div className={`mt-0.5 flex items-center gap-1 text-[10px] font-semibold ${g.level === "red" ? "text-red-600" : "text-amber-600"}`} data-testid={`gecikme-uyari-${r.kalip_kodu}`}>
                                  <AlertTriangle size={11} /> {g.text} — Termin: {trTarih(g.termin)}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-zinc-500 tabular-nums shrink-0 font-semibold">%{pct}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between bg-zinc-50 px-4 py-2.5 text-xs font-semibold border-t border-zinc-100">
                  <span className="text-zinc-500">Şekillenecek: <span className="text-indigo-700 font-bold">{fmt(totSek)}</span></span>
                  <span className="text-zinc-500">Şekillenen: <span className="text-amber-700 font-bold" data-testid={`tot-sekillenen-${name}`}>{fmt(totSekl)}</span></span>
                  <span className="text-zinc-500">Net: <span className="text-green-700 font-bold" data-testid={`tot-net-${name}`}>{fmt(totNet)}</span></span>
                  <span className="text-zinc-500">Toplam Kalan: <span className={`font-bold ${totKalan > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmt(totKalan)}</span></span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SekillendirmePlani() {
  const columns = [
    { key: "kalip_kodu", label: "Kalıp Kodu", mono: true },
    { key: "makina", label: "Makina" },
    { key: "kalip_degisim_tarihi", label: "Kalıp Bağlanma Tarihi", render: (r) => <span className="mono">{trTarih(r.kalip_degisim_tarihi)}</span> },
    { key: "kalip_sokulme_tarihi", label: "Kalıp Sökülme Tarihi", value: (r) => trTarih(r.kalip_sokulme_tarihi),
      render: (r) => r.kalip_sokulme_tarihi
        ? <span className="mono font-semibold text-slate-700" data-testid={`sokulme-${r.id}`}>{trTarih(r.kalip_sokulme_tarihi)}</span>
        : <span className="text-zinc-300">—</span> },
    { key: "calisma_vardiya", label: "Çalışma Vardiya", num: true, value: (r) => gunTxt(r),
      render: (r) => r.calisma_vardiya == null ? <span className="text-zinc-300">—</span>
        : <span className={`font-bold ${r.calisma_devam ? "text-amber-600" : "text-slate-700"}`} data-testid={`calisma-vardiya-${r.id}`}>{gunTxt(r)}</span> },
    {
      key: "tahmini_kalip_degisim_tarihi", label: "Tahmini Kalıp Değişim Tarihi",
      value: (r) => trTarih(r.tahmini_kalip_degisim_tarihi),
      render: (r) => {
        const g = gecikmeInfo(r);
        const cls = !r.tahmini_kalip_degisim_tarihi ? "text-zinc-300"
          : g?.level === "red" ? "text-red-600" : g?.level === "amber" ? "text-amber-600" : "text-purple-700";
        return <span className={`mono font-semibold ${cls}`}>{trTarih(r.tahmini_kalip_degisim_tarihi)}</span>;
      },
    },
    { key: "sekillenecek_adet", label: "Şekillenecek Adet", num: true, render: (r) => <span className="text-indigo-700 font-semibold">{fmt(r.sekillenecek_adet)}</span> },
    {
      key: "sekillenen_sonra", label: "Bağlanma Sonrası Şekillenen", num: true,
      value: (r) => r.sekillenen_sonra ?? 0,
      render: (r) => <span className="text-amber-700 font-semibold">{fmt(r.sekillenen_sonra)}</span>,
    },
    {
      key: "net_sonra", label: "Bağlanma Sonrası Net", num: true,
      value: (r) => r.net_sonra ?? 0,
      render: (r) => <span className="text-green-700 font-semibold">{fmt(r.net_sonra)}</span>,
    },
    {
      key: "kalan_sekillendirme", label: "Kalan Şekillendirme Adeti", num: true,
      value: (r) => r.kalan_sekillendirme ?? 0,
      render: (r) => <span className={`font-bold ${(r.kalan_sekillendirme ?? 0) > 0 ? "text-red-600" : "text-green-700"}`}>{fmt(r.kalan_sekillendirme)}</span>,
    },
    {
      key: "toplam_kalan_siparis", label: "Toplam Kalan Sipariş", num: true,
      value: (r) => r.toplam_kalan_siparis ?? 0,
      render: (r) => <span className="text-red-600 font-bold">{fmt(r.toplam_kalan_siparis)}</span>,
    },
    {
      key: "guncel_biskuvi_stok", label: "Güncel Bisküvi Stoğu", num: true,
      value: (r) => r.guncel_biskuvi_stok ?? 0,
      render: (r) => <span className={`font-bold ${(r.guncel_biskuvi_stok ?? 0) < 0 ? "text-red-600" : "text-sky-700"}`}>{fmt(r.guncel_biskuvi_stok)}</span>,
    },
    {
      key: "ham_stok", label: "Ham Stok", num: true,
      value: (r) => r.ham_stok ?? 0,
      render: (r) => <span data-testid={`ham-stok-${r.kalip_kodu}`} className={`font-bold ${(r.ham_stok ?? 0) < 0 ? "text-red-600" : "text-teal-700"}`}>{fmt(r.ham_stok)}</span>,
    },
    { key: "durum", label: "Durum", value: (r) => durumOf(r), render: (r) => <DurumBadge r={r} testid={`durum-${r.kalip_kodu}`} /> },
  ];
  const fields = [
    { key: "kalip_kodu", label: "Kalıp Kodu", type: "kalipkodu", required: true, span: 2, fills: { aciklama: "aciklama" } },
    { key: "aciklama", label: "Kalıp Açıklaması (otomatik)", type: "text", readOnly: true, span: 2 },
    { key: "makina", label: "Makina (hangi makinaya bağlandı)", type: "text", required: true },
    { key: "kalip_degisim_tarihi", label: "Kalıp Bağlanma Tarihi", type: "date", required: true },
    { key: "kalip_sokulme_tarihi", label: "Kalıp Sökülme Tarihi (girilirse hesap bu tarihte biter)", type: "date" },
    { key: "sekillenecek_adet", label: "Şekillenecek Adet", type: "number", required: true },
  ];
  return (
    <>
      <PageHeader title="Şekillendirme Planı"
        subtitle="Kalıp kodu, makina, değişim tarihi ve şekillenecek adeti girin; bağlanma sonrası şekillenen/net, kalan şekillendirme, toplam kalan sipariş ve güncel bisküvi stoğu otomatik gelir" />
      <CrudTable endpoint="/entity/sekillendirme_plani" listEndpoint="/sekillendirme-plani/list"
        columns={columns} fields={fields} titleSingular="Şekillendirme Planı" printTitle="Şekillendirme Planı"
        dateField="kalip_degisim_tarihi" exportKind="sekillendirme_plani" />
      <MakineBoard />
    </>
  );
}
